import axios, { AxiosRequestConfig } from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import { DateTime } from 'luxon';
import { AuthenticationError } from './apiErrors';
import { NotFoundError } from './errors';

// Regex to match ISO 8601 format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss with optional timezone
// Updated to handle standard ISO format with exactly 3 decimal places for milliseconds
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)?)?$/;

// Update determineApiBaseUrl to handle Node.js environment
export const determineApiBaseUrl = () => {
    const clientUrl = window.location.hostname;

    if (!clientUrl) {
        throw new Error('Unable to determine client URL. Ensure CLIENT_URL is set in the environment variables for Node.js.');
    }

    if (clientUrl.includes('localhost')) {
        return 'https://localhost:7131';
    } else if (clientUrl.includes('polite-grass-0f097790f') || clientUrl.includes('stg.portfoliomatic.ca')) {
        return 'https://api-portfmat-stg-cyfye3e8dqamgpab.canadacentral-01.azurewebsites.net';
    } else if (clientUrl.includes('portfoliomatic.ca')) {
        return 'https://api-portfmat-prod-fwd8fbeef5augzcw.canadacentral-01.azurewebsites.net';
    } else {
        throw new Error(`Unknown environment: Unable to determine API base URL for client URL ${clientUrl}`);
    }
};

const API_BASE_URL = determineApiBaseUrl();

// Fix syntax errors and missing references
let auth0Client: Auth0ContextInterface | null = null;

export function setAuth0Client(client: Auth0ContextInterface): void {
    auth0Client = client;
}

export function isAuth0ClientSet(): boolean {
    return auth0Client !== null;
}

async function getAuthToken(): Promise<string> {
    if (!auth0Client) {
        throw new Error('Auth0 client is not set');
    }

    try {
        const token = await auth0Client.getAccessTokenSilently({
            cacheMode: 'on',
            timeoutInSeconds: 60
        });
        return token;
    } catch (error) {
        console.error('Error getting access token:', error);
        throw error;
    }
}

function isObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function convertDatesToDateObjects<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map((item) => convertDatesToDateObjects(item)) as T;
    }

    if (isObject(obj)) {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
            // Only convert strings that look like actual ISO date/datetime strings
            // This regex matches ISO 8601 format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss with optional timezone
            if (typeof value === 'string' &&
                ISO_DATE_REGEX.test(value) &&
                DateTime.fromISO(value).isValid) {
                result[key] = DateTime.fromISO(value);
            } else {
                result[key] = convertDatesToDateObjects(value);
            }
        }
        return result as T;
    }

    return obj;
}

export async function apiRequest<T = any>(endpoint: string, options: AxiosRequestConfig = {}): Promise<T> {
    if (!isAuth0ClientSet()) {
        throw new Error('Auth0 client is not set. Cannot make API request.');
    }

    try {
        const token = await getAuthToken();
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        };

        const response = await axios({
            url: `${API_BASE_URL}${endpoint}`,
            ...options,
            headers,
        });

        // Skip JSON transformation for binary responses (e.g. file downloads)
        if (options.responseType === 'blob') {
            return response.data as T;
        }

        return convertDatesToDateObjects(response.data) as T;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                throw new AuthenticationError(
                    error.response?.data?.message || 'Authentication required',
                    error.response.status as 401 | 403
                );
            }
            if (error.response?.status === 404) {
                throw new NotFoundError(endpoint, `API request returned 404: ${error.response.statusText || error.message}`);
            }
            throw new Error(error.response?.data?.message || `API request failed: ${error.response?.statusText || error.message}`);
        }
        throw error;
    }
}

export const get = <T = any>(endpoint: string, options?: AxiosRequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'GET' });
}

export const post = <T = any>(endpoint: string, data?: any, options?: AxiosRequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'POST', data });
}

export const put = <T = any>(endpoint: string, data?: any, options?: AxiosRequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'PUT', data });
}

export const del = <T = any>(endpoint: string, options?: AxiosRequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
}
